import React, { useState, useEffect, useRef, useMemo } from 'react';

import { Box, LinearProgress, Typography } from '@mui/material';


// Convert a number of milliseconds into proper mm:ss time format
// If shouldRoundUp is true, rounds up to the next second (for countDOWN timers)
function formatTimerText(milliseconds, secondsRoundUp = false) {
	const totalSeconds = secondsRoundUp ? Math.ceil(milliseconds / 1000) : Math.floor(milliseconds / 1000);
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;

	return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const PROGRESS_SIZE_PX = 3;

/**
 * TimerProgressBar - A configurable timer with progress bar and timer text visualization
 *
 * @param {number} interval - Timer interval in seconds
 * @param {number} updateFrequency - Visual update frequency in milliseconds (100-1000, default: 100)
 * @param {function} onIntervalReached - Callback function when interval is reached
 * @param {boolean} countDown - If true, counts down; if false, counts up (default: false)
 * @param {number} resetTrigger - When this value changes, the timer resets to 0
 */
export default function TimerProgressBar({
	interval,
	onIntervalReached,
	updateFrequency = 100,
	countDown = false,
	resetTrigger = 0
}){
	const [elapsedMillis, setElapsedMillis] = useState(0);
	const [disableTransition, setDisableTransition] = useState(false);

	const startTimeRef = useRef(Date.now());
	const lastTickRef = useRef(0);
	const timerRef = useRef(null);
	const previousIntervalRef = useRef(interval);
	const onIntervalReachedRef = useRef(onIntervalReached);
	const transitionTimeoutRef = useRef(null);

	// Cleanup timer on unmount
	useEffect(() => {
		return () => {
			if (timerRef.current) {
				clearInterval(timerRef.current);
			}
			if (transitionTimeoutRef.current) {
				clearTimeout(transitionTimeoutRef.current);
			}
		};
	}, []);

	// Clamp update frequency between 100ms and 1000ms (memoized)
	const clampedFrequency = useMemo(
		() => Math.max(100, Math.min(1000, updateFrequency)),
		[updateFrequency]
	);

	// Keep callback ref updated
	useEffect(() => {
		onIntervalReachedRef.current = onIntervalReached;
	}, [onIntervalReached]);

	// Reset timer when resetTrigger changes
	useEffect(() => {
		// Disable transition for instant reset
		setDisableTransition(true);

		setElapsedMillis(0);
		startTimeRef.current = Date.now();
		lastTickRef.current = 0;

		// Re-enable transition after a brief moment (50 ms)
		transitionTimeoutRef.current = setTimeout(() => {
			setDisableTransition(false);
		}, 50);

	}, [resetTrigger]);

	// Handle interval changes
	useEffect(() => {
		const intervalMillis = interval * 1000;

		// If interval changed while timer is running
		if (previousIntervalRef.current !== interval && elapsedMillis > 0) {
			if (intervalMillis < elapsedMillis) {
				// New interval is less than elapsed time - trigger immediately
				if (onIntervalReachedRef.current) {
					onIntervalReachedRef.current();
				}

				// Reset timer
				setElapsedMillis(0);
				startTimeRef.current = Date.now();
				lastTickRef.current = 0;
			}
		}

		previousIntervalRef.current = interval;
	}, [interval, elapsedMillis]);

	// Main timer logic
	useEffect(() => {
		// Don't start timer if interval is less than 15 seconds
		if (interval < 15) { return; }

		const intervalMillis = interval * 1000;

		function tick() {
			const now = Date.now();
			const actualElapsed = now - startTimeRef.current + lastTickRef.current;

			if (actualElapsed >= intervalMillis) {
				// Interval reached
				if (onIntervalReachedRef.current) {
					onIntervalReachedRef.current();
				}

				// Disable transition for instant reset
				setDisableTransition(true);

				// Reset timer, but preserve the overflow time for smooth transition
				const overflow = actualElapsed - intervalMillis;
				startTimeRef.current = now - overflow;
				lastTickRef.current = 0;
				setElapsedMillis(overflow);

				// Re-enable transition after a brief moment (50 ms)
				if (transitionTimeoutRef.current) {
					clearTimeout(transitionTimeoutRef.current);
				}
				transitionTimeoutRef.current = setTimeout(() => {
					setDisableTransition(false);
				}, 50);

			} else {
				setElapsedMillis(actualElapsed);
			}
		};

		// Start the timer
		timerRef.current = setInterval(tick, clampedFrequency);

		// Cleanup
		return () => {
			if (timerRef.current) {
				clearInterval(timerRef.current);
			}
		};
	}, [interval, clampedFrequency]);

	// Calculate progress percentage and determine display time based on count direction
	const intervalMillis = interval * 1000;
	
	let progress = 0;
	let displayTimeMs = 0;
	if (intervalMillis > 0) {
		progress = Math.min((elapsedMillis / intervalMillis) * 100, 100);
		displayTimeMs = countDown ? Math.max(0, intervalMillis - elapsedMillis) : elapsedMillis;
	}
	const displayTime = formatTimerText(Math.max(0, displayTimeMs), countDown);

	return (
		<Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
			<Box sx={{ flexGrow: 1 }}>
				<LinearProgress
					variant="determinate"
					value={progress}
					sx={{
						height: PROGRESS_SIZE_PX,
						borderRadius: 1,
						'& .MuiLinearProgress-bar': {
							transition: disableTransition ? 'none' : 'transform 0.4s linear'
						}
					}}
					aria-label={`Timer progress: ${Math.round(progress)}%`}
				/>
			</Box>
			<Typography
				variant="body2"
				sx={{
					minWidth: '45px',
					fontFamily: 'monospace',
					fontWeight: 'bold'
				}}
			>
				{displayTime}
			</Typography>
		</Box>
	);
};